import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicShell } from "@/components/public/PublicChrome";

/* =============================================================================
 * PRICING — same art direction as the landing and features pages
 * (manuscript baseline, Amiri for Arabic, Newsreader display, lapis/gold/
 * harakat palette). src/pages/PricingEn.jsx
 *
 * All the logic is unchanged:
 *  - Route it wrapped in MaybeShell so a logged-in user gets the app Navbar:
 *      <Route path="/pricing" element={<MaybeShell><PricingEn /></MaybeShell>} />
 *    The `user && user.onboarded` check at the bottom must match MaybeShell's
 *    exactly, or both navbars render at once.
 *  - Not logged in -> /auth?mode=signup&next=/pricing?plan=X, then ?plan=X
 *    auto-resumes checkout.
 *  - Logged in -> free column hidden, grid drops to 2 columns.
 *
 * PRICE MISMATCH TO FIX: the comparison table previously said $7/mo and $100
 * lifetime while the cards say $5 and $70. The table now reads from PRICES so
 * it can't drift again.
 * ========================================================================== */

const PRICES = {
  monthly: {
    amount: "$5",
    suffix: "/month",
    perDay: "$0.16/day",
    note: "No commitment.",
  },
  annual: {
    amount: "$4",
    suffix: "/month",
    perDay: "$0.12/day",
    note: "Billed $40 a year, two months free.",
  },
  lifetime: { amount: "$70", suffix: "once", note: "A single payment." },
};

const SUB_FEATURES = [
  "Unlimited reviews and every practice mode",
  "Full oasis customisation",
  "Grammar, conjugation and root practice",
  "Support answered inside 24h",
];
const FREE_FEATURES = [
  "Alphabet and vocabulary, with daily limits",
  "7 days of full premium first",
  "No card needed",
  "Yours to keep",
];
const LIFETIME_FEATURES = [
  "Everything in Premium, permanently",
  "Cheaper than eighteen months of monthly",
  "Lifetime badge and exclusive oasis items",
  "All future updates included",
];

const COMPARE_COLS = [
  { key: "harf", label: "Harf", price: "$4–5/mo", highlight: true },
  { key: "duo", label: "Duolingo", price: "≈$9.99/mo" },
  { key: "italki", label: "iTalki", price: "≈$20/hour" },
  { key: "rosetta", label: "Rosetta Stone", price: "≈$18/mo" },
];

const CHECK = "check",
  CROSS = "cross",
  DASH = "dash";
const COMPARE_ROWS = [
  {
    label: "Alphabet and script practice",
    harf: CHECK,
    duo: DASH,
    italki: CHECK,
    rosetta: CROSS,
  },
  {
    label: "Spaced repetition",
    harf: CHECK,
    duo: CROSS,
    italki: CROSS,
    rosetta: CROSS,
  },
  {
    label: "Triliteral roots",
    harf: CHECK,
    duo: CROSS,
    italki: DASH,
    rosetta: CROSS,
  },
  { label: "Grammar", harf: CHECK, duo: CROSS, italki: CHECK, rosetta: DASH },
  {
    label: "CEFR coverage",
    harf: "A1–C2",
    duo: DASH,
    italki: CROSS,
    rosetta: CROSS,
  },
  {
    label: "Free tier",
    harf: CHECK,
    duo: CHECK,
    italki: "trial only",
    rosetta: "trial only",
  },
  {
    label: "Lifetime plan",
    harf: PRICES.lifetime.amount,
    duo: CROSS,
    italki: CROSS,
    rosetta: "≈$140",
  },
  {
    label: "Support response",
    harf: "< 24h",
    duo: "varies",
    italki: "instant",
    rosetta: "varies",
  },
];

const MILESTONES = [
  ["10,000", "1 free month"],
  ["100,000", "6 free months"],
  ["500,000", "1 free year"],
  ["1,000,000", "Lifetime access"],
];

const TESTIMONIALS = [
  {
    i: "LF",
    name: "Léa Fontaine",
    loc: "France",
    title: "Zero to B1 material in two months",
    body: "I had never written a letter of Arabic. Two months later I was working through B1 vocabulary without help.",
  },
  {
    i: "AC",
    name: "Amelia Clarke",
    loc: "United Kingdom",
    title: "It replaced Duolingo in a week",
    body: "No filler, no guilt-trip notifications. The reviews land right at the edge of forgetting.",
  },
  {
    i: "DK",
    name: "Daniel Kim",
    loc: "United States",
    title: "Nothing else was complete",
    body: "Four other apps, each missing a piece. This one has the audio, the grammar and the order.",
  },
  {
    i: "JP",
    name: "Julien Petit",
    loc: "France",
    title: "Two years of circles, fixed",
    body: "I never knew what to study next. The roadmap sorted that in a week.",
  },
  {
    i: "OI",
    name: "Omar Idrissi",
    loc: "Morocco",
    title: "Not built to farm my attention",
    body: "Cheapest serious Arabic tool I found, and the only one that does not treat me like a slot machine.",
  },
  {
    i: "KB",
    name: "Karim Belkacem",
    loc: "Algeria",
    title: "The developer answers",
    body: "I asked a question on Discord and had a real answer the same evening.",
  },
];

const FAQ = [
  {
    q: "What's included in the 7-day trial?",
    a: "Complete access to every Premium feature: unlimited vocabulary, grammar, conjugation and letter learning, unlimited SRS reviews and practice, full CEFR content from A1 to C2, oasis building and customisation, every practice mode, and advanced progress tracking. No card required to start.",
  },
  {
    q: "What happens when the trial ends?",
    a: "You either subscribe, or you continue on a free account with daily limits: 5 vocabulary words, 2 grammar points and 100 SRS reviews per day. Letter and conjugation practice stay unlimited. Free accounts have view-only access to the oasis and limited access to the premium practice modes. You are never charged unless you choose to subscribe.",
  },
  {
    q: "Is the Lifetime plan really lifetime?",
    a: "It is a one-time payment covering all current and future Premium features, a lifetime badge, exclusive oasis items and no recurring charge. Access is valid for as long as Harf operates. All plans are subject to our Terms of Service.",
  },
  {
    q: "Can I cancel whenever I want?",
    a: "Yes. A monthly or yearly subscription can be cancelled at any time from your account and runs to the end of the period you already paid for. Lifetime purchases carry a 30-day money-back guarantee.",
  },
  {
    q: "Are prices VAT included?",
    a: "Yes, every price shown includes VAT. What you see is what you pay at checkout.",
  },
  {
    q: "I am a complete beginner. Is this for me?",
    a: "Yes. Onboarding assesses your level and starts you on the alphabet if you are new, then the roadmap carries you from the basics through to advanced content at your own pace.",
  },
];

function Cell({ v }) {
  if (v === CHECK) return <span className="c-yes">✓</span>;
  if (v === CROSS) return <span className="c-no">×</span>;
  if (v === DASH) return <span className="c-dash" />;
  return <span className="c-txt">{v}</span>;
}

function PricingContent() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [yearly, setYearly] = useState(true);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const autoResumed = useRef(false);
  const showFreeTier = !user;

  const [urls, setUrls] = useState([""]);
  const [submitting, setSubmitting] = useState(false);
  const [claims, setClaims] = useState([]);

  useEffect(() => {
    document.title = "Pricing — Harf, learn Arabic";
  }, []);

  const startCheckout = async (plan) => {
    setError("");
    if (!user) {
      nav(
        `/auth?mode=signup&next=${encodeURIComponent(`/pricing?plan=${plan}`)}`,
      );
      return;
    }
    setLoading(plan);
    try {
      const { data } = await api.post("/pricing/checkout", { plan });
      if (data?.url) window.location.href = data.url;
      else throw new Error("Missing URL");
    } catch {
      setError(
        "Checkout couldn't start. Try again in a moment, or email info@learnharf.app.",
      );
      setLoading(null);
    }
  };

  useEffect(() => {
    if (autoResumed.current || !user) return;
    const plan = new URLSearchParams(loc.search).get("plan");
    if (plan && ["monthly", "annual", "lifetime"].includes(plan)) {
      autoResumed.current = true;
      if (plan === "annual") setYearly(true);
      if (plan === "monthly") setYearly(false);
      startCheckout(plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const sub = yearly ? PRICES.annual : PRICES.monthly;

  const addUrl = () => setUrls([...urls, ""]);
  const removeUrl = (index) => setUrls(urls.filter((_, i) => i !== index));
  const updateUrl = (index, val) => {
    const newUrls = [...urls];
    newUrls[index] = val;
    setUrls(newUrls);
  };
  const submitClaim = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      setClaims([
        {
          id: Date.now(),
          video_urls: urls,
          verified_views: 0,
          status: "pending",
        },
        ...claims,
      ]);
      setUrls([""]);
      setSubmitting(false);
    }, 800);
  };

  return (
    <div className="hf-price">
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Inter:wght@400;500;600&display=swap');

.hf-price {
  --paper: #ECEDF1;
  --paper-deep: #E2E4EA;
  --ink: #14172A;
  --ink-soft: #5D6179;
  --lapis: #1F3FA8;
  --gold: #B0862F;
  --harakat: #B8412E;
  --rule: #C9CCD6;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.hf-price * { box-sizing: border-box; }
.hf-price h1, .hf-price h2, .hf-price h3 { font-family: 'Newsreader', Georgia, serif; font-weight: 500; letter-spacing: -0.015em; margin: 0; }
.hf-price p { margin: 0; }
.hf-price ul { list-style: none; margin: 0; padding: 0; }
.hf-price a { color: inherit; text-decoration: none; }
.hf-price :focus-visible { outline: 2px solid var(--lapis); outline-offset: 3px; }
.hf-price .ar { font-family: 'Amiri', 'Noto Naskh Arabic', serif; direction: rtl; }
.hf-price .wrap { max-width: 1120px; margin: 0 auto; padding: 0 28px; }
.hf-price .narrow { max-width: 860px; }
.hf-price .tick { display: inline-block; width: 26px; height: 1px; background: var(--harakat); vertical-align: 6px; margin-right: 12px; }
.hf-price .band { padding: 70px 0; border-top: 1px solid var(--rule); }
.hf-price .band h2 { font-size: clamp(24px, 3vw, 32px); margin-bottom: 6px; }
.hf-price .band-note { color: var(--ink-soft); font-size: 14.5px; margin-bottom: 34px; max-width: 52ch; }

/* HEAD */
.hf-price .ph { padding: 96px 0 0; }
.hf-price .ph-grid { display: grid; grid-template-columns: minmax(0, 6fr) minmax(0, 5fr); gap: 52px; align-items: end; }
.hf-price .ph h1 { font-size: clamp(36px, 5.4vw, 60px); line-height: 1.04; letter-spacing: -0.03em; max-width: 14ch; }
.hf-price .ph .lede { margin-top: 20px; color: var(--ink-soft); font-size: 16.5px; max-width: 48ch; }
.hf-price .ph-glyph { font-size: clamp(110px, 15vw, 180px); line-height: .8; color: var(--lapis); opacity: .1; text-align: center; user-select: none; }
.hf-price .ph-rule { margin-top: 48px; height: 1px; background: linear-gradient(90deg, var(--ink) 0 22%, var(--rule) 22% 100%); }

/* LAUNCH NOTE */
.hf-price .launch { margin-top: 34px; border-left: 2px solid var(--gold); padding: 4px 0 4px 22px; max-width: 62ch; }
.hf-price .launch p { font-family: 'Newsreader', Georgia, serif; font-size: 17px; line-height: 1.5; }
.hf-price .launch cite { display: block; margin-top: 10px; font-style: normal; font-size: 13px; color: var(--ink-soft); }

.hf-price .alert { margin: 24px auto 0; max-width: 1120px; padding: 12px 28px; border-left: 2px solid var(--harakat); color: var(--harakat); font-size: 14px; }

/* PLANS */
.hf-price .plans { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--ink); }
.hf-price .plans.two { grid-template-columns: repeat(2, 1fr); max-width: 760px; }
.hf-price .plan { border-left: 1px solid var(--rule); padding: 30px 28px 34px; display: flex; flex-direction: column; }
.hf-price .plan:first-child { border-left: none; padding-left: 0; }
.hf-price .plan.feature { background: var(--ink); color: var(--paper); border-left-color: var(--ink); padding-left: 28px; }
.hf-price .plan h3 { font-size: 22px; }
.hf-price .plan-sub { font-size: 13.5px; color: var(--ink-soft); margin-top: 4px; min-height: 38px; }
.hf-price .plan.feature .plan-sub, .hf-price .plan.feature .plan-meta, .hf-price .plan.feature li { color: #A7ACC4; }
.hf-price .plan-amount { font-family: 'Newsreader', Georgia, serif; font-size: 50px; line-height: 1; letter-spacing: -0.03em; margin-top: 18px; }
.hf-price .plan-amount span { font-family: 'Inter', sans-serif; font-size: 13px; color: var(--ink-soft); letter-spacing: 0; margin-left: 6px; }
.hf-price .plan.feature .plan-amount span { color: #A7ACC4; }
.hf-price .plan-meta { font-size: 12.5px; color: var(--ink-soft); margin-top: 8px; }
.hf-price .toggle { display: flex; align-items: center; gap: 10px; margin-top: 16px; font-size: 13px; }
.hf-price .toggle button { width: 38px; height: 20px; border: 1px solid var(--lapis); border-radius: 20px; background: transparent; display: flex; align-items: center; padding: 2px; cursor: pointer; }
.hf-price .toggle span.knob { width: 13px; height: 13px; border-radius: 50%; background: var(--lapis); transition: transform .18s ease; }
.hf-price .toggle.month span.knob { transform: translateX(17px); }
.hf-price .toggle .on { font-weight: 600; color: var(--ink); }
.hf-price .toggle .off { color: var(--ink-soft); }
.hf-price .plan-cta { margin-top: 26px; width: 100%; padding: 13px 18px; border-radius: 2px; font-size: 14.5px; font-weight: 600; cursor: pointer; border: 1px solid var(--ink); background: transparent; color: var(--ink); display: flex; align-items: center; justify-content: center; gap: 8px; transition: background .18s ease, color .18s ease; }
.hf-price .plan-cta:hover { background: var(--ink); color: var(--paper); }
.hf-price .plan.feature .plan-cta { background: var(--paper); border-color: var(--paper); color: var(--ink); }
.hf-price .plan.feature .plan-cta:hover { background: var(--gold); border-color: var(--gold); }
.hf-price .plan-cta:disabled { opacity: .5; cursor: default; }
.hf-price .spin { width: 14px; height: 14px; border: 2px solid currentColor; border-top-color: transparent; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.hf-price .plan-list { margin-top: 24px; }
.hf-price .plan-list li { font-size: 14px; color: var(--ink-soft); padding: 9px 0; border-top: 1px solid var(--rule); display: grid; grid-template-columns: 16px 1fr; gap: 8px; }
.hf-price .plan.feature .plan-list li { border-top-color: rgba(255,255,255,.14); }
.hf-price .plan-list li::before { content: "✓"; color: var(--gold); font-size: 13px; }
.hf-price .plan-flag { display: inline-block; margin-top: 14px; font-size: 11.5px; color: var(--gold); border: 1px solid var(--gold); border-radius: 2px; padding: 1px 7px; }
.hf-price .plan-fine { font-size: 12px; color: var(--ink-soft); margin-top: 10px; }

/* TABLE */
.hf-price .tbl-scroll { overflow-x: auto; }
.hf-price table { width: 100%; min-width: 620px; border-collapse: collapse; font-size: 14px; }
.hf-price th, .hf-price td { padding: 13px 14px; border-bottom: 1px solid var(--rule); text-align: center; }
.hf-price th:first-child, .hf-price td:first-child { text-align: left; padding-left: 0; width: 30%; }
.hf-price thead th { border-bottom: 1px solid var(--ink); font-weight: 600; font-size: 14.5px; vertical-align: bottom; }
.hf-price thead th small { display: block; font-weight: 400; font-size: 12px; color: var(--ink-soft); margin-top: 3px; }
.hf-price .col-harf { background: var(--paper-deep); color: var(--lapis); }
.hf-price .c-yes { color: var(--lapis); font-size: 15px; }
.hf-price .c-no { color: var(--rule); font-size: 15px; }
.hf-price .c-dash { display: inline-block; width: 14px; height: 1px; background: var(--rule); vertical-align: middle; }
.hf-price .c-txt { font-size: 12.5px; color: var(--ink-soft); }
.hf-price .tbl-fine { font-size: 12px; color: var(--ink-soft); margin-top: 14px; }

/* CREATORS */
.hf-price .miles { display: grid; grid-template-columns: repeat(4, 1fr); margin: 0 0 30px; border-top: 1px solid var(--rule); }
.hf-price .mile { border-left: 1px solid var(--rule); padding: 16px 18px; }
.hf-price .mile:first-child { border-left: none; padding-left: 0; }
.hf-price .mile-v { font-family: 'Newsreader', Georgia, serif; font-size: 25px; line-height: 1.1; }
.hf-price .mile-r { font-size: 13px; color: var(--lapis); margin-top: 3px; }
.hf-price .field { display: flex; gap: 8px; margin-bottom: 10px; }
.hf-price .field input { flex: 1; border: 1px solid var(--rule); border-radius: 2px; background: #fff; padding: 11px 14px; font-size: 14px; font-family: inherit; color: var(--ink); }
.hf-price .field input:focus { outline: none; border-color: var(--lapis); }
.hf-price .btn-min { border: 1px solid var(--rule); background: transparent; border-radius: 2px; padding: 9px 14px; font-size: 13.5px; font-weight: 500; cursor: pointer; color: var(--ink); font-family: inherit; }
.hf-price .btn-min:hover { border-color: var(--ink); }
.hf-price .btn-solid { background: var(--ink); color: var(--paper); border: 1px solid var(--ink); border-radius: 2px; padding: 11px 22px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 8px; }
.hf-price .btn-solid:hover { background: var(--lapis); border-color: var(--lapis); }
.hf-price .btn-solid:disabled { opacity: .5; }
.hf-price .row-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 6px; }
.hf-price .claim { border-top: 1px solid var(--rule); padding: 13px 0; display: flex; justify-content: space-between; gap: 16px; font-size: 13.5px; color: var(--ink-soft); flex-wrap: wrap; }
.hf-price .claim b { color: var(--gold); font-weight: 600; }
.hf-price .gate { border-left: 2px solid var(--rule); padding-left: 20px; }
.hf-price .gate p { font-size: 14.5px; color: var(--ink-soft); margin: 6px 0 16px; max-width: 52ch; }

/* TESTIMONIALS */
.hf-price .t-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 44px; }
.hf-price .t-card { border-top: 1px solid var(--rule); padding: 24px 0 28px; }
.hf-price .t-title { font-family: 'Newsreader', Georgia, serif; font-size: 18.5px; line-height: 1.25; margin-bottom: 9px; }
.hf-price .t-body { font-size: 14.5px; color: var(--ink-soft); }
.hf-price .t-foot { display: flex; align-items: center; gap: 10px; margin-top: 16px; }
.hf-price .t-mark { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--lapis); color: var(--lapis); font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; }
.hf-price .t-name { font-size: 13.5px; font-weight: 600; }
.hf-price .t-loc { font-size: 12.5px; color: var(--ink-soft); }

/* FAQ */
.hf-price .faq { max-width: 760px; }
.hf-price .faq-item { border-top: 1px solid var(--rule); }
.hf-price .faq-item:last-child { border-bottom: 1px solid var(--rule); }
.hf-price .faq-q { width: 100%; display: flex; align-items: baseline; justify-content: space-between; gap: 24px; padding: 19px 0; background: none; border: none; color: var(--ink); text-align: left; cursor: pointer; font-family: 'Newsreader', Georgia, serif; font-size: 18px; }
.hf-price .faq-sign { color: var(--harakat); transition: transform .22s ease; }
.hf-price .faq-item.open .faq-sign { transform: rotate(45deg); }
.hf-price .faq-a { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .26s ease; }
.hf-price .faq-item.open .faq-a { grid-template-rows: 1fr; }
.hf-price .faq-a > div { overflow: hidden; }
.hf-price .faq-a p { font-size: 14.5px; color: var(--ink-soft); max-width: 66ch; padding-bottom: 20px; }

@media (max-width: 900px) {
  .hf-price .ph { padding-top: 68px; }
  .hf-price .ph-grid { grid-template-columns: 1fr; gap: 26px; }
  .hf-price .ph-glyph { display: none; }
  .hf-price .plans, .hf-price .plans.two { grid-template-columns: 1fr; }
  .hf-price .plan { border-left: none; border-top: 1px solid var(--rule); padding-left: 0; padding-right: 0; }
  .hf-price .plan:first-child { border-top: none; }
  .hf-price .plan.feature { padding: 30px 24px; }
  .hf-price .plan-sub { min-height: 0; }
  .hf-price .miles { grid-template-columns: 1fr 1fr; }
  .hf-price .mile:nth-child(3) { border-left: none; padding-left: 0; }
  .hf-price .t-grid { grid-template-columns: 1fr 1fr; gap: 0 30px; }
  .hf-price .band { padding: 54px 0; }
}
@media (max-width: 620px) {
  .hf-price .t-grid, .hf-price .miles { grid-template-columns: 1fr; }
  .hf-price .mile { border-left: none; padding-left: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hf-price * { animation: none !important; transition: none !important; }
}
            `}</style>

      {/* HEAD */}
      <header className="ph">
        <div className="wrap">
          <div className="ph-grid">
            <div>
              <h1>One price, the whole language</h1>
              <p className="lede">
                Letters, 4,000 words, grammar and conjugation, A1 to C2. Seven
                days free first, no card.
              </p>
              <div className="launch">
                <p>
                  Harf just launched and not every feature is live yet. So
                  whoever subscribes now keeps this price for as long as they
                  stay.
                </p>
                <cite>Théo, who builds Harf</cite>
              </div>
            </div>
            <div className="ph-glyph ar" aria-hidden="true">
              سعر
            </div>
          </div>
          <div className="ph-rule" />
        </div>
      </header>

      {error && (
        <p role="alert" className="alert">
          {error}
        </p>
      )}

      {/* PLANS */}
      <section className="band" style={{ borderTop: "none" }}>
        <div className="wrap">
          <div className={`plans${showFreeTier ? "" : " two"}`}>
            <div className="plan">
              <h3>Subscription</h3>
              <p className="plan-sub">Every feature, billed how you prefer.</p>
              <div className="plan-amount">
                {sub.amount}
                <span>{sub.suffix}</span>
              </div>
              <div className="plan-meta">{sub.perDay} · VAT included</div>
              <div className={`toggle${yearly ? "" : " month"}`}>
                <span className={yearly ? "on" : "off"}>Yearly</span>
                <button
                  role="switch"
                  aria-checked={!yearly}
                  aria-label="Toggle yearly or monthly billing"
                  onClick={() => setYearly((y) => !y)}
                >
                  <span className="knob" />
                </button>
                <span className={yearly ? "off" : "on"}>Monthly</span>
              </div>
              <div className="plan-meta">{sub.note}</div>
              <button
                onClick={() => startCheckout(yearly ? "annual" : "monthly")}
                disabled={loading !== null}
                data-testid="checkout-subscription"
                className="plan-cta"
              >
                {loading === (yearly ? "annual" : "monthly") && (
                  <span className="spin" />
                )}
                Subscribe
              </button>
              <ul className="plan-list">
                {SUB_FEATURES.map((f) => (
                  <li key={f}>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            {showFreeTier && (
              <div className="plan feature">
                <h3>Start free</h3>
                <p className="plan-sub">
                  Seven days of full access, then free with daily limits.
                </p>
                <div className="plan-amount">
                  $0<span>to begin</span>
                </div>
                <div className="plan-meta">No card, nothing to cancel.</div>
                <Link
                  to="/auth?mode=signup"
                  data-testid="checkout-free"
                  className="plan-cta"
                >
                  Create my account
                </Link>
                <ul className="plan-list">
                  {FREE_FEATURES.map((f) => (
                    <li key={f}>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="plan">
              <h3>Pay once</h3>
              <p className="plan-sub">Your copy, permanently.</p>
              <div className="plan-amount">
                {PRICES.lifetime.amount}
                <span>{PRICES.lifetime.suffix}</span>
              </div>
              <div className="plan-meta">
                {PRICES.lifetime.note} VAT included.
              </div>
              <span className="plan-flag">launch price</span>
              <button
                onClick={() => startCheckout("lifetime")}
                disabled={loading !== null}
                data-testid="checkout-lifetime"
                className="plan-cta"
              >
                {loading === "lifetime" && <span className="spin" />}
                Buy lifetime
              </button>
              <div className="plan-fine">30-day money-back guarantee</div>
              <ul className="plan-list">
                {LIFETIME_FEATURES.map((f) => (
                  <li key={f}>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="band">
        <div className="wrap">
          <h2>
            <span className="tick" />
            Against the alternatives
          </h2>
          <p className="band-note">
            Public pricing as of 2026. Swipe sideways on a phone.
          </p>
          <div className="tbl-scroll">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  {COMPARE_COLS.map((c) => (
                    <th key={c.key} className={c.highlight ? "col-harf" : ""}>
                      {c.label}
                      <small>{c.price}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((r) => (
                  <tr key={r.label}>
                    <td>{r.label}</td>
                    <td className="col-harf">
                      <Cell v={r.harf} />
                    </td>
                    <td>
                      <Cell v={r.duo} />
                    </td>
                    <td>
                      <Cell v={r.italki} />
                    </td>
                    <td>
                      <Cell v={r.rosetta} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="tbl-fine">
            Competitor information comes from publicly available data and may
            change.
          </p>
        </div>
      </section>

      {/* CREATORS */}
      <section className="band">
        <div className="wrap narrow">
          <h2>
            <span className="tick" />
            Earn premium with your videos
          </h2>
          <p className="band-note">
            Publish a video about Harf, send us the link, and we verify the
            cumulative views. The milestone you reach sets how long you get
            premium for. It is access, not a payment.
          </p>
          <div className="miles">
            {MILESTONES.map(([views, reward]) => (
              <div key={views} className="mile">
                <div className="mile-v">{views}</div>
                <div className="mile-r">{reward}</div>
              </div>
            ))}
          </div>

          {!user ? (
            <div className="gate">
              <h3 style={{ fontSize: 19 }}>Claiming needs an account</h3>
              <p>
                Create a free one, then add your links and tell us when the
                views are in.
              </p>
              <button
                type="button"
                className="btn-solid"
                onClick={() =>
                  nav("/auth?mode=signup", {
                    state: { from: "/pricing" },
                  })
                }
              >
                Create my free account
              </button>
            </div>
          ) : (
            <form onSubmit={submitClaim}>
              <p className="plan-meta" style={{ marginBottom: 14 }}>
                Linked email: {user?.email}
              </p>
              {urls.map((url, index) => (
                <div key={index} className="field">
                  <input
                    required
                    type="url"
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                    placeholder="https://youtube.com/… or a TikTok / Instagram link"
                  />
                  <button
                    type="button"
                    className="btn-min"
                    onClick={() => removeUrl(index)}
                    aria-label="Remove link"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div className="row-actions">
                <button type="button" className="btn-min" onClick={addUrl}>
                  Add another video
                </button>
                <button
                  disabled={submitting}
                  type="submit"
                  className="btn-solid"
                >
                  {submitting && <span className="spin" />}
                  Submit for review
                </button>
              </div>
            </form>
          )}

          {claims.length > 0 && (
            <div style={{ marginTop: 30 }}>
              <h3 style={{ fontSize: 19, marginBottom: 6 }}>My claims</h3>
              {claims.map((claim) => (
                <div key={claim.id} className="claim">
                  <span>
                    {claim.video_urls?.length ?? 0} video(s) ·{" "}
                    {(claim.verified_views ?? 0).toLocaleString("en-US")}{" "}
                    verified views
                  </span>
                  <b>
                    {claim.status === "approved"
                      ? `Access granted (${claim.reward_plan})`
                      : claim.status === "rejected"
                        ? "Rejected"
                        : "Pending verification"}
                  </b>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="band">
        <div className="wrap">
          <h2>
            <span className="tick" />
            They had already tried the others
          </h2>
          <p className="band-note">
            Most learners arrive after abandoning two or three apps.
          </p>
          <div className="t-grid">
            {TESTIMONIALS.map((t, idx) => (
              <div key={idx} className="t-card">
                <div className="t-title">{t.title}</div>
                <div className="t-body">{t.body}</div>
                <div className="t-foot">
                  <div className="t-mark">{t.i}</div>
                  <div>
                    <div className="t-name">{t.name}</div>
                    <div className="t-loc">{t.loc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Link
            to="/features"
            style={{
              display: "inline-block",
              marginTop: 30,
              fontSize: 14.5,
              fontWeight: 600,
              color: "var(--lapis)",
              borderBottom: "1px solid var(--lapis)",
              paddingBottom: 2,
            }}
          >
            See everything included
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="band">
        <div className="wrap">
          <h2>
            <span className="tick" />
            Before you pay
          </h2>
          <p className="band-note">Anything else goes to info@learnharf.app.</p>
          <div className="faq">
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={f.q} className={`faq-item${open ? " open" : ""}`}>
                  <button
                    className="faq-q"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? null : i)}
                  >
                    <span>{f.q}</span>
                    <span className="faq-sign">+</span>
                  </button>
                  <div className="faq-a">
                    <div>
                      <p>{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export default function PricingEn() {
  const { user } = useAuth();
  // Keep this condition identical to MaybeShell's in App.jsx
  // (`user && user.onboarded`), or you'll get both navbars at once.
  if (user && user.onboarded) return <PricingContent />;
  return (
    <PublicShell className="pricing-shell">
      <PricingContent />
    </PublicShell>
  );
}
